import type { AttendancePolicyResponse } from '../api/types';
import type { SelfiePolicy } from './attendance-readiness';

/**
 * Kebijakan absensi — bagian MURNI, tanpa React.
 *
 * Layar ini adalah pintu hari pertama sebuah organisasi. Tanpa kebijakan aktif, server menolak
 * SETIAP absen masuk dengan `ATT-001`, dan aplikasinya tidak dapat dipakai sama sekali. Sampai
 * sekarang satu-satunya jalan membuatnya adalah menjalankan `provision.seed.ts` terhadap basis
 * data produksi — yakni insinyur, untuk setiap pelanggan.
 */

export interface FormKebijakan {
  readonly timezone: string;
  readonly workDays: readonly number[];
  readonly workStartTime: string;
  readonly workEndTime: string;
  readonly lateToleranceMinutes: number;
  readonly minGpsAccuracyMeters: number;
  readonly selfiePolicy: SelfiePolicy;
  readonly allowCheckInOutsideGeofence: boolean;
  readonly outsideGeofenceRequiresApproval: boolean;
  readonly outsideGeofenceRequiresReason: boolean;
  readonly fieldWorkGeofenceExempt: boolean;
  readonly allowCheckInOnNonWorkDays: boolean;
  readonly dayCloseCutoffHours: number;
  readonly maxEarlyCheckInMinutes: number;
}

/**
 * Nilai bawaan, diambil dari yang dipakai `provision.seed.ts` untuk organisasi SUNGGUHAN.
 *
 * Bukan angka yang saya karang: `08:00`–`17:00` dan `Asia/Jakarta` adalah nilai bawaan skrip
 * provisioning, dan Senin–Jumat mengikuti `work_days` yang sudah dipakai Emguard. Organisasi baru
 * harus bisa menekan Simpan tanpa memahami `dayCloseCutoffHours` lebih dulu — itu inti layar ini.
 */
export const KEBIJAKAN_BAWAAN: FormKebijakan = {
  timezone: 'Asia/Jakarta',
  workDays: [1, 2, 3, 4, 5],
  workStartTime: '08:00',
  workEndTime: '17:00',
  lateToleranceMinutes: 15,
  minGpsAccuracyMeters: 100,
  // Item 9, review 12 Sep 2026 — PEMBALIKAN dari OPTIONAL, mengikuti bawaan baru
  // `provision.seed.ts`. Hanya bawaan formulir organisasi TANPA kebijakan aktif; organisasi
  // yang sudah punya kebijakan (mis. Emguard) menyunting dari nilainya sendiri lewat
  // `formDariKebijakan`, tidak pernah menyentuh konstanta ini.
  selfiePolicy: 'REQUIRED',
  allowCheckInOutsideGeofence: false,
  outsideGeofenceRequiresApproval: true,
  outsideGeofenceRequiresReason: true,
  fieldWorkGeofenceExempt: true,
  // Item 8, review 12 Sep 2026 — permisif seperti perilaku sebelum kolom ini ada; organisasi
  // baru tidak dikunci ke aturan yang lebih ketat tanpa memilihnya secara sadar.
  allowCheckInOnNonWorkDays: true,
  dayCloseCutoffHours: 6,
  maxEarlyCheckInMinutes: 60,
};

const POLA_JAM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface MasalahKebijakan {
  readonly bidang: string;
  readonly pesan: string;
}

/**
 * Aturan yang sama dengan yang ditegakkan `provision-input.ts`, TERMASUK yang paling penting.
 *
 * **Tanpa lokasi kerja DAN tanpa izin absen di luar geofence, organisasi menolak SETIAP absen
 * masuk.** Skrip provisioning menolak menghasilkan keadaan itu dan menuntut pemanggil memilih
 * secara sadar. Layar ini menegakkan aturan yang sama — kalau tidak, manajer dapat membuat lewat
 * UI persis keadaan yang skrip tolak, lalu seluruh timnya gagal absen esok paginya tanpa satu pun
 * pesan yang menjelaskan sebabnya.
 *
 * Jam lintas tengah malam juga ditolak, dengan alasan yang sama seperti di skrip: shift semacam
 * itu belum didukung, dan menerimanya menghasilkan hari kerja yang tidak pernah selesai.
 */
export function periksaKebijakan(
  form: FormKebijakan,
  jumlahLokasiKerja: number,
): readonly MasalahKebijakan[] {
  const masalah: MasalahKebijakan[] = [];

  if (form.workDays.length === 0) {
    masalah.push({ bidang: 'workDays', pesan: 'Pilih minimal satu hari kerja.' });
  }
  if (!POLA_JAM.test(form.workStartTime)) {
    masalah.push({ bidang: 'workStartTime', pesan: 'Jam mulai harus berbentuk HH:MM.' });
  }
  if (!POLA_JAM.test(form.workEndTime)) {
    masalah.push({ bidang: 'workEndTime', pesan: 'Jam selesai harus berbentuk HH:MM.' });
  }
  if (
    POLA_JAM.test(form.workStartTime) &&
    POLA_JAM.test(form.workEndTime) &&
    form.workStartTime >= form.workEndTime
  ) {
    masalah.push({
      bidang: 'workEndTime',
      pesan: 'Jam selesai harus setelah jam mulai. Shift melewati tengah malam belum didukung.',
    });
  }
  if (form.timezone.trim().length === 0) {
    masalah.push({ bidang: 'timezone', pesan: 'Zona waktu wajib diisi.' });
  }

  /*
   * INI YANG PALING MAHAL BILA TERLEWAT, dan satu-satunya aturan di sini yang bergantung pada
   * keadaan di luar formulir.
   */
  if (jumlahLokasiKerja === 0 && !form.allowCheckInOutsideGeofence) {
    masalah.push({
      bidang: 'allowCheckInOutsideGeofence',
      pesan:
        'Belum ada lokasi kerja, dan absen di luar geofence tidak diizinkan — dengan kebijakan ini ' +
        'SETIAP absen masuk akan ditolak. Tambahkan lokasi kerja lebih dulu, atau izinkan absen di ' +
        'luar geofence bila titiknya memang belum diketahui.',
    });
  }

  return masalah;
}

/**
 * Badan permintaan `POST /api/attendance-policies` — TANPA `as`.
 *
 * Server memakai `forbidNonWhitelisted`: satu field asing membatalkan SELURUH permintaan dengan
 * 400, dan tidak ada typecheck yang berkeberatan sampai orang menekan Simpan. Cast akan
 * menyembunyikan persis itu — kelas cacat yang sama dengan `dropoff` pada jalur tarik DO.
 */
export function bodiKebijakan(form: FormKebijakan): {
  timezone: string;
  workDays: number[];
  workStartTime: string;
  workEndTime: string;
  lateToleranceMinutes: number;
  minGpsAccuracyMeters: number;
  selfiePolicy: SelfiePolicy;
  allowCheckInOutsideGeofence: boolean;
  outsideGeofenceRequiresApproval: boolean;
  outsideGeofenceRequiresReason: boolean;
  fieldWorkGeofenceExempt: boolean;
  allowCheckInOnNonWorkDays: boolean;
  dayCloseCutoffHours: number;
  maxEarlyCheckInMinutes: number;
} {
  return {
    timezone: form.timezone.trim(),
    // Diurutkan supaya "Sen, Rab, Sel" yang diklik acak tidak tersimpan sebagai urutan acak.
    workDays: [...form.workDays].sort((a, b) => a - b),
    workStartTime: form.workStartTime,
    workEndTime: form.workEndTime,
    lateToleranceMinutes: form.lateToleranceMinutes,
    minGpsAccuracyMeters: form.minGpsAccuracyMeters,
    selfiePolicy: form.selfiePolicy,
    allowCheckInOutsideGeofence: form.allowCheckInOutsideGeofence,
    outsideGeofenceRequiresApproval: form.outsideGeofenceRequiresApproval,
    outsideGeofenceRequiresReason: form.outsideGeofenceRequiresReason,
    fieldWorkGeofenceExempt: form.fieldWorkGeofenceExempt,
    allowCheckInOnNonWorkDays: form.allowCheckInOnNonWorkDays,
    dayCloseCutoffHours: form.dayCloseCutoffHours,
    maxEarlyCheckInMinutes: form.maxEarlyCheckInMinutes,
  };
}

/** Kebijakan yang sudah aktif → bentuk formulir, supaya menyunting tidak dimulai dari kosong. */
export function formDariKebijakan(p: AttendancePolicyResponse): FormKebijakan {
  return {
    timezone: p.timezone,
    workDays: p.workDays,
    workStartTime: p.workStartTime,
    workEndTime: p.workEndTime,
    lateToleranceMinutes: p.lateToleranceMinutes,
    minGpsAccuracyMeters: p.minGpsAccuracyMeters,
    selfiePolicy: p.selfiePolicy,
    allowCheckInOutsideGeofence: p.allowCheckInOutsideGeofence,
    outsideGeofenceRequiresApproval: p.outsideGeofenceRequiresApproval,
    outsideGeofenceRequiresReason: p.outsideGeofenceRequiresReason,
    fieldWorkGeofenceExempt: p.fieldWorkGeofenceExempt,
    allowCheckInOnNonWorkDays: p.allowCheckInOnNonWorkDays,
    dayCloseCutoffHours: p.dayCloseCutoffHours,
    maxEarlyCheckInMinutes: p.maxEarlyCheckInMinutes,
  };
}

/** Nama hari untuk lencana pemilih. Indeks 1 = Senin, mengikuti nilai yang diterima server. */
export const NAMA_HARI: Readonly<Record<number, string>> = {
  1: 'Sen',
  2: 'Sel',
  3: 'Rab',
  4: 'Kam',
  5: 'Jum',
  6: 'Sab',
  7: 'Min',
};
